import React from 'react';

import ColourCheckbox from '~/components/ColourCheckbox';
import Tracked from '~/components/Tracked';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import { brandLogos } from '../brandLogos';
import type { MenuItemProps } from '../types';
import {
  BRAND_LOGO_CLASS_NAME,
  BRAND_LOGO_IMAGE_CLASS_NAME,
  ITEM_TEXT_CLASS_NAME,
  LABEL_LINK_CLASS_NAME,
  LINK_CLASS_NAME,
  MENU_LIST_ITEM_CLASS_NAME,
  menuListClassName,
  SECONDARY_LINK_CLASS_NAME,
  SUBMENU_TRIGGER_CLASS_NAME
} from './classNames';
import { AngleRight } from './MobileMenuDrawer';

interface MobileSubMenuProps {
  ancestorLabels: string[];
  closeMenu: () => void;
  idsStack: MenuItemProps[];
  menu: MenuItemProps[];
  nestingLevel: number;
  parentName: string;
  path: string;
  selectItem: (item: MenuItemProps) => void;
  selected: MenuItemProps | null;
}

export default function MobileSubMenu({
  selected = null,
  parentName,
  closeMenu,
  selectItem,
  menu,
  idsStack,
  path,
  nestingLevel = 1,
  ancestorLabels
}: MobileSubMenuProps) {
  const fireNavigationLink = useNavigationLinkInteraction('phone_menu');
  const isExpanded =
    !!idsStack.find((x) => x.name === parentName) &&
    selected?.name !== parentName;
  const menuTypes = ['filter', 'label'];
  const initialOffset = 370;
  const listStyle =
    !!selected && selected.name === parentName
      ? {
          left: `${nestingLevel * initialOffset}px`
        }
      : undefined;

  return (
    <ul
      className={menuListClassName({
        isExpanded,
        isShown: !!selected && selected.name === parentName
      })}
      style={listStyle}
    >
      {menu.map((menuItem, index: number) => {
        const leafHref =
          menuItem?.link ||
          (menuItem?.slug?.includes('?') && `${path}${menuItem?.slug}`) ||
          `${path}${menuItem?.slug}/`;
        function fire(href: string) {
          return fireNavigationLink(href, index + 1, {
            ancestorLabels,
            label: menuItem.name
          });
        }

        return (
          <React.Fragment key={`${parentName}-${menuItem.name}-${index}`}>
            {menuItem.type === 'filter' && (
              <li className={MENU_LIST_ITEM_CLASS_NAME}>
                <Tracked.ExternalLink
                  className={SECONDARY_LINK_CLASS_NAME}
                  name={menuItem.name as TrackedButtonClickName}
                  href={menuItem.link || ''}
                  variant="none"
                  pagePosition="Header"
                  onClick={() => {
                    fire(menuItem.link || '');
                    closeMenu();
                  }}
                >
                  {menuItem.name}
                </Tracked.ExternalLink>
              </li>
            )}
            {menuItem.type === 'label' && (
              <li className={MENU_LIST_ITEM_CLASS_NAME}>{menuItem.name}</li>
            )}
            {(!menuItem.children || !menuItem.children.length) &&
              !menuTypes.includes(menuItem.type) && (
                <li className={MENU_LIST_ITEM_CLASS_NAME}>
                  {menuItem.colour && (
                    <div className="mr-[10px] mt-[5px]">
                      <ColourCheckbox
                        isRefined={false}
                        fill={menuItem.colour}
                      />
                    </div>
                  )}

                  <Tracked.ExternalLink
                    variant="none"
                    name={menuItem.name as TrackedButtonClickName}
                    pagePosition="Header"
                    className={
                      menuItem.type === 'link'
                        ? LABEL_LINK_CLASS_NAME
                        : LINK_CLASS_NAME
                    }
                    href={leafHref}
                    onClick={() => {
                      fire(leafHref);
                      closeMenu();
                    }}
                  >
                    {menuItem.type === 'brand' && (
                      <div className={BRAND_LOGO_CLASS_NAME}>
                        <img
                          alt={menuItem.name}
                          src={brandLogos[menuItem.icon]}
                          loading="lazy"
                          className={BRAND_LOGO_IMAGE_CLASS_NAME}
                        />
                      </div>
                    )}
                    <div className={ITEM_TEXT_CLASS_NAME}>{menuItem.name}</div>
                  </Tracked.ExternalLink>
                </li>
              )}

            {!!menuItem.children && !!menuItem.children.length && (
              <>
                <li className={MENU_LIST_ITEM_CLASS_NAME}>
                  <button
                    type="button"
                    onClick={() => selectItem(menuItem)}
                    className={SUBMENU_TRIGGER_CLASS_NAME}
                  >
                    {menuItem.type === 'brand' && (
                      <div className={BRAND_LOGO_CLASS_NAME}>
                        <img
                          alt={menuItem.name}
                          src={brandLogos[menuItem.icon]}
                          loading="lazy"
                          className={BRAND_LOGO_IMAGE_CLASS_NAME}
                        />
                      </div>
                    )}
                    <div className={ITEM_TEXT_CLASS_NAME}>{menuItem.name}</div>
                    <span>{!!menuItem.children.length && <AngleRight />}</span>
                  </button>
                </li>
                <MobileSubMenu
                  ancestorLabels={[...ancestorLabels, menuItem.name]}
                  selected={selected}
                  parentName={menuItem.name}
                  closeMenu={closeMenu}
                  selectItem={selectItem}
                  menu={menuItem.children || []}
                  idsStack={idsStack}
                  path={menuItem.path}
                  nestingLevel={nestingLevel + 1}
                />
              </>
            )}
          </React.Fragment>
        );
      })}
    </ul>
  );
}
